import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  getCurrentTeamUserList,
  type CurrentTeamUserListResponse,
  type UserListVO,
} from "@ifa/utils";

interface AdvisorStoreState {
  advisors: UserListVO[];
  loaded: boolean;
  loading: boolean;
  fetchCurrentTeamAdvisors: (force?: boolean) => Promise<UserListVO[]>;
  getAdvisorByUid: (uid?: number | null) => UserListVO | undefined;
  clear: () => void;
}

const PAGE_SIZE = 200;

export const useAdvisorStore = create<AdvisorStoreState>()(
  persist(
    (set, get) => ({
      advisors: [],
      loaded: false,
      loading: false,

      fetchCurrentTeamAdvisors: async (force = false) => {
        const { loaded, loading, advisors } = get();

        if (!force && (loaded || loading)) {
          return advisors;
        }

        set({ loading: true });

        try {
          let pageNum = 1;
          let totalPages = 1;
          const advisorMap = new Map<number, UserListVO>();

          do {
            const response = await getCurrentTeamUserList({
              pageNum,
              pageSize: PAGE_SIZE,
            });
            const pageData = response as CurrentTeamUserListResponse;
            const content = pageData.content || [];

            content.forEach((advisor) => {
              if (advisor.uid != null) {
                advisorMap.set(advisor.uid, advisor);
              }
            });

            totalPages = Math.max(pageData.totalPages || 1, 1);

            if (pageData.last || content.length === 0) {
              break;
            }

            pageNum += 1;
          } while (pageNum <= totalPages);

          const nextAdvisors = Array.from(advisorMap.values());
          set({
            advisors: nextAdvisors,
            loaded: true,
            loading: false,
          });

          return nextAdvisors;
        } catch (error) {
          console.error("加載當前團隊顧問列表失敗:", error);
          set({ loading: false });
          return get().advisors;
        }
      },

      getAdvisorByUid: (uid) => {
        if (uid == null) return undefined;
        return get().advisors.find((advisor) => advisor.uid === uid);
      },

      clear: () => {
        set({
          advisors: [],
          loaded: false,
          loading: false,
        });
      },
    }),
    {
      name: "order-current-team-advisors",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        advisors: state.advisors,
        loaded: state.loaded,
      }),
    },
  ),
);
